import { createSelector } from 'reselect'

import { emptyObj } from 'util/objects'

import { selectCurrentTicketLinkedIntegrationsResources } from 'ducks/integrations/selectors'

const selectLinearBase = state =>
  state?.integrations?.thirdPartyData?.linear || {}

export const selectLinearTeamsState = state =>
  selectLinearBase(state).teams || emptyObj

export const selectLinearTeamsById = createSelector(
  selectLinearTeamsState,
  teams => teams.byId || emptyObj
)

export const selectLinearTeams = createSelector(selectLinearTeamsState, teams =>
  (teams.ids || []).map(id => teams.byId && teams.byId[id]).filter(Boolean)
)

export const selectLinearTeamsAsOptions = createSelector(
  selectLinearTeams,
  teams =>
    teams.map(({ id, key, name }) => ({
      key: id,
      text: key ? `${name} (${key})` : name,
      value: id,
    }))
)

export const selectLinearWorkflowStatesByTeamId = (state, teamId) =>
  selectLinearBase(state).workflowStates?.byTeamId?.[teamId] || emptyObj

export const selectLinearWorkflowStatesAsOptionsForTeam = createSelector(
  selectLinearWorkflowStatesByTeamId,
  workflowStates =>
    (workflowStates.ids || [])
      .map(id => workflowStates.byId && workflowStates.byId[id])
      .filter(Boolean)
      .map(({ id, name, type }) => ({
        key: id,
        text: name,
        value: id,
        type,
      }))
)

export const selectLinearIssuesById = createSelector(
  selectLinearBase,
  linear => linear.issues?.byId || emptyObj
)

function resolveLinearIssue(issuesById, reference) {
  if (!reference) return null
  const issue = issuesById[reference]
  if (issue) return issue
  return Object.values(issuesById).find(
    candidate =>
      candidate &&
      (candidate.identifier === reference || candidate.url === reference)
  )
}

function isLinearProvider(provider) {
  return provider === 'linear' || provider === 'LINEAR'
}

export const selectCurrentTicketLinkedIssues = createSelector(
  selectCurrentTicketLinkedIntegrationsResources,
  selectLinearIssuesById,
  (linkedResources, issuesById) => {
    if (!linkedResources) return linkedResources
    return linkedResources
      .filter(
        ({ provider, removed }) =>
          isLinearProvider(provider) && removed !== true
      )
      .map(linkedResource => {
        const { externalId } = linkedResource
        const issue = resolveLinearIssue(issuesById, externalId)
        return issue
          ? {
              ...linkedResource,
              ...issue,
            }
          : {
              ...linkedResource,
              loaded: true,
            }
      })
      .filter(Boolean)
  }
)

export const selectCurrentTicketLinkedIssuesById = createSelector(
  selectCurrentTicketLinkedIssues,
  issues => {
    if (!issues) return issues
    return issues.reduce((byId, issue) => {
      const key = issue.id || issue.externalId
      if (!key) return byId
      return {
        ...byId,
        [key]: issue,
      }
    }, {})
  }
)
